// Per-PO "This payment" split over the PO's OR lines, typed on the payment
// create/edit form and sent as `lines` (ITEM + EXTRA). One state per PO:
//   { purchaseId, total, deliveryFee, paidByOthers, pendingByOthers, lines: [{ orLineId, lineValue, remaining, paidByOthers, pendingByOthers, linePaid, lineUnpaid, value }], extras: [{ key, extraType, description, amount }] }
// `total` is the amount paid to that PO; `remaining` is the line's capacity
// from GET /payment-requests/line-capacity (the max this PR may put on it).

import { splitByWeight } from "../budgetAllocation"

export const EXTRA_TYPES = [
  { value: "DELIVERY_FEE", label: "Delivery fee" },
  { value: "PENALTY", label: "Penalty" },
  { value: "SURCHARGE", label: "Surcharge" },
  { value: "OTHER", label: "Other" },
]

const num = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v))
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi)
const sum = (list, key) => list.reduce((s, x) => s + num(x[key]), 0)

let extraSeq = 0
export const newExtra = (extraType = "OTHER", amount = 0, description = "") => {
  extraSeq += 1
  return { key: `extra-${extraSeq}`, extraType, description, amount }
}

const defaultExtraType = (state) => (num(state.deliveryFee) > 0 ? "DELIVERY_FEE" : "OTHER")

export const seedFromCapacity = (capacity) => {
  const state = {
    purchaseId: String(capacity.purchaseId),
    total: num(capacity.amount),
    deliveryFee: num(capacity.deliveryFee),
    paidByOthers: num(capacity.paidByOthers),
    pendingByOthers: num(capacity.pendingByOthers),
    lines: (capacity.lines || []).map((l) => ({
      orLineId: String(l.orLineId),
      lineValue: num(l.lineValue),
      remaining: num(l.remaining),
      paidByOthers: num(l.paidByOthers),
      pendingByOthers: num(l.pendingByOthers),
      linePaid: l.linePaid == null ? null : num(l.linePaid),
      lineUnpaid: l.lineUnpaid == null ? null : num(l.lineUnpaid),
      value: num(l.suggestedAmount),
    })),
    extras: [],
  }
  const extra = num(capacity.suggestedExtraAmount)
  return extra > 0 ? { ...state, extras: [newExtra(defaultExtraType(state), extra)] } : state
}

// Edit form: start from what the PR already stored (budget-allocations rows
// with allocationSource STORED) instead of BE's default split.
export const seedFromStored = (capacity, storedRows) => {
  const base = seedFromCapacity({ ...capacity, suggestedExtraAmount: 0 })
  // A "Chưa phân phối" row covers several OR lines (`orLineIds`) — spread its
  // value back by each line's value.
  const lineValue = new Map(base.lines.map((l) => [l.orLineId, l.lineValue || 0]))
  const byLine = new Map()
  const add = (id, v) => byLine.set(id, (byLine.get(id) || 0) + v)
  storedRows
    .filter((r) => r.lineType !== "EXTRA")
    .forEach((r) => {
      const ids = r.orLineIds?.length ? r.orLineIds : [String(r.orLineId)]
      if (ids.length === 1) return add(ids[0], num(r.value))
      return splitByWeight(num(r.value), ids.map((id) => lineValue.get(id) || 0)).forEach((v, i) => add(ids[i], v))
    })
  return {
    ...base,
    lines: base.lines.map((l) => ({ ...l, value: byLine.get(l.orLineId) ?? 0 })),
    extras: storedRows
      .filter((r) => r.lineType === "EXTRA")
      .map((r) => newExtra(r.extraType || "OTHER", num(r.value), r.description || "")),
  }
}

export const allocatedOf = (state) => sum(state.lines, "value") + sum(state.extras, "amount")

export const linesFull = (state) => state.lines.every((l) => l.value >= l.remaining)

// The typed line is clamped to [0, remaining]; the PO's payment amount becomes
// lines + extras.
export const editLine = (state, orLineId, raw) => {
  const idx = state.lines.findIndex((l) => l.orLineId === String(orLineId))
  if (idx < 0) return state
  const lines = state.lines.map((l, i) => (i === idx ? { ...l, value: clamp(num(raw), 0, l.remaining) } : l))
  return { ...state, lines, total: sum(lines, "value") + sum(state.extras, "amount") }
}

// Extras sit on top of the lines, so changing one moves the payment amount.
export const editExtra = (state, key, patch) => {
  const extras = state.extras.map((e) =>
    e.key === key ? { ...e, ...patch, ...("amount" in patch ? { amount: Math.max(0, num(patch.amount)) } : {}) } : e
  )
  return { ...state, extras, total: state.total + sum(extras, "amount") - sum(state.extras, "amount") }
}

export const addExtra = (state) =>
  linesFull(state) ? { ...state, extras: [...state.extras, newExtra(defaultExtraType(state), 0)] } : state

export const removeExtra = (state, key) => {
  const removed = state.extras.find((e) => e.key === key)
  return {
    ...state,
    extras: state.extras.filter((e) => e.key !== key),
    total: state.total - num(removed?.amount),
  }
}

export const allocationIssue = (state) => {
  if (sum(state.extras, "amount") > 0 && !linesFull(state)) return "Extra amounts are only allowed once every line is full."
  const gap = state.total - allocatedOf(state)
  if (gap > 0) return `${gap.toLocaleString("en-US")} not allocated yet — add it to a line or lower the payment amount.`
  if (gap < 0) return `Over-allocated by ${(-gap).toLocaleString("en-US")}.`
  return null
}

export const toBodyLines = (state) => [
  ...state.lines.map((l) => ({ purchaseId: state.purchaseId, lineType: "ITEM", orLineId: l.orLineId, amount: l.value })),
  ...state.extras
    .filter((e) => num(e.amount) > 0)
    .map((e) => ({
      purchaseId: state.purchaseId,
      lineType: "EXTRA",
      extraType: e.extraType,
      description: e.description?.trim() || undefined,
      amount: num(e.amount),
    })),
]
